import { TELANGANA_DISTRICTS, telanganaDistrictKeyFromStored } from "./telanganaDistricts";
import { normalizeGeoLabel } from "../utils/geoLabel";

/** Stable DB row id for references (e.g. FK from applications). */
export type TgRegistrationVillage = { id: number; name: string; code: string };

export type TgRegistrationMandal = {
  id: number;
  mandalCode: string;
  mandalName: string;
  villages: TgRegistrationVillage[];
};

export type TgRegistrationDistrictRow = {
  /** Present on live API rows: primary key is `registration_district_code` (string); omitted in the offline bundle. */
  id?: number | string;
  districtKey: string;
  registrationDistrictCode: string;
  registrationDistrictName: string;
  mandals: TgRegistrationMandal[];
  error?: string;
};

export type GeoDirectoryPayload = {
  source: string;
  generatedAt: string;
  districts: TgRegistrationDistrictRow[];
};

export type GeoDirectoryStore = {
  raw: GeoDirectoryPayload;
  tgRegistrationDistricts(): TgRegistrationDistrictRow[];
  tgMandalsForDistrict(key: string): TgRegistrationMandal[];
  tgVillagesForMandal(districtKey: string, mandalName: string): TgRegistrationVillage[];
  /** Prefer mandal + village match; if none, first village in the district with the same normalised name. */
  tgResolveVillageDbIdInDistrict(
    districtKey: string,
    mandalName: string,
    villageName: string,
  ): number | undefined;
  tgDirectoryAggregateCounts(): { districts: number; mandals: number; villages: number };
  tgDirectoryCountsForDistrict(districtKey: string): { mandals: number; villages: number };
  tgVillageCountForMandal(districtKey: string, mandalName: string): number;
  findDistrict(keyOrName: string): TgRegistrationDistrictRow | undefined;
};

export function createGeoDirectoryStore(data: GeoDirectoryPayload): GeoDirectoryStore {
  const raw = data;

  function tgRegistrationDistricts(): TgRegistrationDistrictRow[] {
    return raw.districts.filter((d) => !d.error && Array.isArray(d.mandals));
  }

  /**
   * API/DB `districtKey` can differ in case or (rarely) from the canonical slug; registration name
   * may still match the map label. Prefer rows that actually have mandal data.
   */
  function pickDistrictRow(rows: TgRegistrationDistrictRow[]): TgRegistrationDistrictRow | undefined {
    if (rows.length === 0) return undefined;
    if (rows.length === 1) return rows[0];
    return [...rows].sort((a, b) => b.mandals.length - a.mandals.length)[0];
  }

  function resolveDirectoryDistrict(k: string): TgRegistrationDistrictRow | undefined {
    const list = tgRegistrationDistricts();
    const key = String(k).trim();
    if (!key) return undefined;
    const exactMatches = list.filter((x) => String(x.districtKey ?? "").trim() === key);
    const exact = pickDistrictRow(exactMatches);
    if (exact) return exact;
    const ciMatches = list.filter(
      (x) => String(x.districtKey ?? "").trim().toLowerCase() === key.toLowerCase(),
    );
    const ci = pickDistrictRow(ciMatches);
    if (ci) return ci;
    const wantName = normalizeGeoLabel(key);
    const nameHits = list.filter((x) => normalizeGeoLabel(x.registrationDistrictName) === wantName);
    if (nameHits.length === 1) return nameHits[0];
    if (nameHits.length > 1) {
      return [...nameHits].sort((a, b) => b.mandals.length - a.mandals.length)[0];
    }
    const slug = telanganaDistrictKeyFromStored(key);
    const label = slug ? TELANGANA_DISTRICTS.find((d) => d.key === slug)?.label : undefined;
    if (label) {
      const want = normalizeGeoLabel(label);
      const labelHits = list.filter((x) => normalizeGeoLabel(x.registrationDistrictName) === want);
      if (labelHits.length === 1) return labelHits[0];
      if (labelHits.length > 1) {
        return [...labelHits].sort((a, b) => b.mandals.length - a.mandals.length)[0];
      }
    }
    return undefined;
  }

  function tgMandalsForDistrict(key: string): TgRegistrationMandal[] {
    const d = resolveDirectoryDistrict(key);
    if (!d?.mandals || d.error) return [];
    return d.mandals;
  }

  function tgVillagesForMandal(districtKey: string, mandalName: string): TgRegistrationVillage[] {
    const nm = normalizeGeoLabel(mandalName);
    const m = tgMandalsForDistrict(districtKey).find((x) => normalizeGeoLabel(x.mandalName) === nm);
    return m?.villages ?? [];
  }

  function tgDirectoryAggregateCounts(): { districts: number; mandals: number; villages: number } {
    const districts = tgRegistrationDistricts();
    let mandals = 0;
    let villages = 0;
    for (const d of districts) {
      mandals += d.mandals.length;
      for (const m of d.mandals) {
        villages += m.villages.length;
      }
    }
    return { districts: districts.length, mandals, villages };
  }

  function tgDirectoryCountsForDistrict(districtKey: string): {
    mandals: number;
    villages: number;
  } {
    const mandals = tgMandalsForDistrict(districtKey);
    let villages = 0;
    for (const m of mandals) {
      villages += m.villages.length;
    }
    return { mandals: mandals.length, villages };
  }

  function tgVillageCountForMandal(districtKey: string, mandalName: string): number {
    return tgVillagesForMandal(districtKey, mandalName).length;
  }

  function tgResolveVillageDbIdInDistrict(
    districtKey: string,
    mandalName: string,
    villageName: string,
  ): number | undefined {
    const vn = normalizeGeoLabel(villageName);
    const fromMandal = tgVillagesForMandal(districtKey, mandalName);
    const hit = fromMandal.find((x) => normalizeGeoLabel(x.name) === vn);
    if (hit) return hit.id;
    for (const m of tgMandalsForDistrict(districtKey)) {
      const h = m.villages.find((x) => normalizeGeoLabel(x.name) === vn);
      if (h) return h.id;
    }
    return undefined;
  }

  return {
    raw,
    tgRegistrationDistricts,
    tgMandalsForDistrict,
    tgVillagesForMandal,
    tgResolveVillageDbIdInDistrict,
    tgDirectoryAggregateCounts,
    tgDirectoryCountsForDistrict,
    tgVillageCountForMandal,
    findDistrict: resolveDirectoryDistrict,
  };
}

/** Match a stored district string (slug, directory key, or registration name) to a geo directory row key. */
export function storedDistrictMatchesDirectoryKey(
  store: GeoDirectoryStore,
  directoryKey: string,
  storedDistrict: string | null | undefined,
): boolean {
  const stored = String(storedDistrict ?? "").trim();
  if (!stored) return false;
  const row = store.findDistrict(directoryKey);
  if (row) {
    if (normalizeGeoLabel(row.districtKey) === normalizeGeoLabel(stored)) return true;
    if (normalizeGeoLabel(row.registrationDistrictName) === normalizeGeoLabel(stored)) return true;
  }
  const fromStored = telanganaDistrictKeyFromStored(stored);
  const fromFilter = telanganaDistrictKeyFromStored(directoryKey);
  if (fromStored && fromFilter && fromStored === fromFilter) return true;
  return normalizeGeoLabel(directoryKey) === normalizeGeoLabel(stored);
}

export const TG_REGISTRATION_SOURCE = (payload: GeoDirectoryPayload) => payload.source;
export const TG_REGISTRATION_GENERATED_AT = (payload: GeoDirectoryPayload) => payload.generatedAt;
